/**
 * Deps Detector Service
 * Detect project types and package managers
 */
import * as fs from 'fs/promises';
import * as path from 'path';
import type { DetectedProject, Ecosystem, PackageManager } from '../types/deps.types.js';

export class DepsDetectorService {
    /**
     * Detect all project types in the given directory
     */
    async detectProjects(cwd: string): Promise<DetectedProject[]> {
        const projects: DetectedProject[] = [];

        // 1. Check Node.js
        if (await this.fileExists(cwd, 'package.json')) {
            const manager = await this.detectNodePackageManager(cwd);
            projects.push({
                ecosystem: 'node',
                manager,
                configFile: 'package.json'
            });
        }

        // 2. Check PHP
        if (await this.fileExists(cwd, 'composer.json') || await this.fileExists(cwd, 'composer.lock')) {
            const isLaravel = await this.detectLaravel(cwd);
            projects.push({
                ecosystem: 'php',
                manager: 'composer',
                isLaravel,
                configFile: 'composer.json'
            });
        }

        // 3. Check Python
        if (await this.fileExists(cwd, 'Pipfile')) {
            projects.push({
                ecosystem: 'python',
                manager: 'pipenv',
                configFile: 'Pipfile'
            });
        } else if (await this.fileExists(cwd, 'requirements.txt')) {
            projects.push({
                ecosystem: 'python',
                manager: 'pip',
                configFile: 'requirements.txt'
            });
        }

        return projects;
    }

    /**
     * Detect Node.js package manager
     */
    private async detectNodePackageManager(cwd: string): Promise<PackageManager> {
        // Check lockfiles first
        if (await this.fileExists(cwd, 'pnpm-lock.yaml')) {
            return 'pnpm';
        }
        if (await this.fileExists(cwd, 'yarn.lock')) {
            return 'yarn';
        }
        if (await this.fileExists(cwd, 'bun.lockb')) {
            return 'bun';
        }
        if (await this.fileExists(cwd, 'package-lock.json')) {
            return 'npm';
        }

        // Check environment
        const userAgent = process.env.npm_config_user_agent || '';
        if (userAgent.includes('pnpm')) return 'pnpm';
        if (userAgent.includes('yarn')) return 'yarn';
        if (userAgent.includes('bun')) return 'bun';

        // Default to npm
        return 'npm';
    }

    /**
     * Detect if project is Laravel
     */
    private async detectLaravel(cwd: string): Promise<boolean> {
        try {
            const composerPath = path.join(cwd, 'composer.json');
            const content = await fs.readFile(composerPath, 'utf-8');
            const composerJson = JSON.parse(content);
            
            const deps = {
                ...composerJson.require,
                ...composerJson['require-dev']
            };
            
            return 'laravel/framework' in deps;
        } catch {
            return false;
        }
    }

    /**
     * Check if file exists
     */
    private async fileExists(cwd: string, filename: string): Promise<boolean> {
        try {
            await fs.access(path.join(cwd, filename));
            return true;
        } catch {
            return false;
        }
    }
}
